class EnterDataFormController {

    constructor(
        $uibModal,
        $window,
        DatetimeHelper,
        EnterDataFormService,
        FacilitySettings
    ){
        'ngInject';

        this.$uibModal = $uibModal;
        this.$window = $window;
        this.DatetimeHelper = DatetimeHelper;
        this.EnterDataFormService = EnterDataFormService;
        this.FacilitySettings = FacilitySettings;
    }

    $onInit() {
        this.errors = null;
        this.isEditMode = (this.savedFormData.committed !== 1);
        this.isFormSaved = false;
        this.isFormCommitted = false;

        this.areStatsSaving = false;
        this.areFacilitySettingsSaving = false;

        this.showSavedWarning = (
            'version_num' in this.savedFormData
            && this.savedFormData.version_num != null
            && this.isEditMode);

        this.lastEditDate = this.DatetimeHelper.getLocalDatetimeFromUtc(this.savedFormData.last_updated);
        this.formMetadata.map((fm) => fm['isEmpty'] = this.savedFormData[fm['stats_column_name']] == null);

        this.orderBy = this.savedFormData.is_mer_import == 1 && this.savedFormData.version_num == 1
            ? ['!isEmpty', 'display_order'] : 'display_order';

        this.selectedRelatedModels = {};
        this.configureFacilitySettings();

        this.includedFacilitySettings = this.EnterDataFormService.getFormSettings();
        this.displayProps = this.FacilitySettings.displayProps;
        this.units = this.FacilitySettings.units;

        this.startAutoSavePolling();
    }

    $onDestroy() {
        clearInterval(this.autoSavePoll);
    }

    /***
     * Sets up some additional parameters that are specific to the facility that is being displayed. Includes:
     * 1) setting which value is currently saved for each of the related dropdown selectors
     * 2) translates the boolean list of Days of Operation into a list of Days object IDs
     * 3) populates all of the checkbox fields to check the values currently saved in the database
     */
    configureFacilitySettings() {
        this.selectedRelatedModels = this.FacilitySettings.populateSelectedRelatedModels(this.facilityFields,
                                                                                         this.relatedModels,
                                                                                         this.facilitySettings);

        this.FacilitySettings.populateSelectedCheckboxItems(this.facilityFields,
                                                            this.relatedModels,
                                                            this.facilitySettings);

        this.previousSettings = angular.copy(this.facilitySettings);
    }

    /***
     * Callback function that is triggered whenever a dropdown option is selected.
     *
     * @param field     the "name" property of the Facility Setting that is being selected.
     * @param item      an object representing the item that was selected.
     */
    selectDropdownOption(field, item) {
        this.selectedRelatedModels[field] = item;
    }

    /***
     * Get the HTML input type for the given stats column.
     *
     * @param formField     the stats column for the given form field (e.g. item_n_001)
     *
     * @returns {String}
     */
    getInputTypeFromField(formField) {

        const column_name = formField.stats_column_name;

        if (column_name.indexOf('item_n') > -1) {
            return 'number';
        }
        else if (column_name.indexOf('item_t') > -1) {
            return 'text';
        }
        else if (column_name.indexOf('item_d') > -1) {
            return 'datetime-local';
        }
        else if (column_name.indexOf('item_e') > -1) {
            return 'enum';
        }
        else {
            return 'text';
        }
    }

    /***
     * Hides the notifications that show up after thee form is saved or committed.
     */
    hideFormSubmittedAlerts() {
            this.isFormSaved = false;
            this.isFormCommitted = false;
    }

    /***
     * Returns whether the value passed in is an array. Used for determining whether to display
     * an error or whether to it is an array of validation errors that must be iterated over.
     *
     * @param value     a Javascript variable.
     *
     * @returns {boolean}
     */
    isArray(value) {
        return Array.isArray(value);
    }

    /***
     * Open the Survey Settings modal.
     */
    openConfirmationModal() {
        this.$uibModal.open({
            component: 'ChangeReasonModal',
            size: 'md',
            windowClass: 'custom-modal change-reason-modal',
            resolve: {
                changeReasonList: () => this.changeReasonList,
                parentController: () => this
            }
        });
    }

    saveWithChange(changeReason, explanation) {
        this.savedFormData.stats_change_reason = changeReason.id;
        this.savedFormData.stats_change_reason_detail = explanation;
        this.commitForm();
        this.saveFacilitySettings();
    }

    /***
     * Create an interval that backs up the current savedFormData every 5 minutes (300000 seconds). If the user is
     * logged out due to inactivity, the most recent backup will be saved to the database.
     */
    startAutoSavePolling() {
        this.autoSavePoll = setInterval(() => {
            this.EnterDataFormService.updateUnsavedData(this.savedFormData);
        }, 300000)
    }

    /***
     * Switches the form from a display mode where its values are static to an editable mode where
     * they are forms that the user can update.
     */
    startEditMode() {
        const message = "Data for this month has already been committed. Are you sure you want to " +
            "edit that data?";
        const doEdit = confirm(message);
        if (doEdit) {
            this.isEditMode = true;
        }
    }

    /***
     * Saves a draft of the current form data.
     */
    saveForm() {
        this.areStatsSaving = true;
        this.errors = null;
        this.saveFacilitySettings(false);

        this.savedFormData.is_active = 1;
        this.EnterDataFormService.saveStats(this.savedFormData)
            .then((successResponse) => {
                const previousStatsId = 'id' in this.savedFormData ? this.savedFormData.id : null;

                this.savedFormData.id = successResponse.data.id;
                this.savedFormData.version_num = successResponse.data.version_num;

                this.isFormSaved = true;
                this.isFormCommitted = false;
                this.showSavedWarning = (this.savedFormData.committed !== 1);
            },
            (errorResponse) => {
                this.errors = errorResponse.data;
                this.hideFormSubmittedAlerts();
                this.$window.scrollTo(0, 0);
            });
    }

    /***
     * Called when the "commit" button is clicked. If the data has already been committed, opens up a modal that
     * requires the user to provide an explanation for why they are changing the data. If this is a first commit, then
     * we bypass the modal and immediately commit the data.
     */
    commitButtonClick() {
        if (this.savedFormData.committed) {
            this.openConfirmationModal();
        }
        else {
            this.commitForm();
            this.saveFacilitySettings(true);
        }
    }

    /***
     * Toggles off edit mode if BOTH the stats data and the facility settings have successfully saved. Otherwise,
     */
    toggleEditMode() {
        this.isEditMode = (this.areStatsSaving || this.areFacilitySettingsSaving);
    }

     /***
     * Commits the current form data, locking the data in (though it can still be edited after
     * the fact).
     */
    commitForm() {
        this.areStatsSaving = true;
        this.errors = null;

        this.savedFormData.is_active = 1;
        this.savedFormData.committed = 1;
        this.EnterDataFormService.saveStats(this.savedFormData)
            .then((successResponse) =>{
                const previousStatsId = 'id' in this.savedFormData ? this.savedFormData.id : null;

                this.savedFormData.id = successResponse.data.id;
                this.savedFormData.version_num = successResponse.data.version_num;

                this.areStatsSaving = false;
                this.isFormSaved = false;
                this.isFormCommitted = true;
                this.showSavedWarning = false;
                this.toggleEditMode();
            },
            (errorResponse) => {
                this.errors = errorResponse.data;
                this.hideFormSubmittedAlerts();
                this.$window.scrollTo(0, 0);
            });
    }

    /***
     * Checks whether any FacilitySettings have been changed. If so, the fnew values are passed up to the API to commit
     * them to the database.
     *
     * Returns early if there is nothing to update.
     */
    saveFacilitySettings(forceAllFields) {
        this.areFacilitySettingsSaving = true;
        this.errors = null;

        const settingsToSave = this.FacilitySettings.getSettingsToSave(this.facilityFields,
                                                                       this.facilitySettings,
                                                                       this.previousSettings,
                                                                       this.selectedRelatedModels,
                                                                       forceAllFields);

        if (Object.keys(settingsToSave).length === 0) {
            this.areFacilitySettingsSaving = false;
            this.toggleEditMode();
            return;
        }

        const data = {
            month: this.savedFormData.month,
            year: this.savedFormData.year,
            facility_setting_values: settingsToSave
        };

        this.FacilitySettings.updateFacilitySettings(this.savedFormData.facility, data)
            .then((successResponse) => {
                this.previousSettings = angular.copy(this.facilitySettings);
                this.areFacilitySettingsSaving = false;
                this.toggleEditMode();
            }, (errorResponse) => {
                this.errors = errorResponse.data;
                this.hideFormSubmittedAlerts();
                this.$window.scrollTo(0, 0);
            });
    }

    /***
     * Checks that a value supplied has a value greater than zero. sets to blank if true
     * @param newValue: the value being checked against
     * @param fieldName: the fieldname that is being checked
     */
    validateNumber(newValue, fieldName) {
        if (newValue < 0) {
            alert("Please do not enter a value less than zero.");
            this.facilitySettings[fieldName] = null;
        }
    }


}

export default EnterDataFormController;


// WEBPACK FOOTER //
// ./app/components/enter-data/enter-data-form/enter-data-form-controller.js